// Frontend mirror of src/utils/indiaStates.js — keep the two in step.
//
// The GST state codes, for every form on the site. A state code decides
// CGST+SGST vs IGST on every invoice, so it is not a free-text field: it is
// picked from this list, or DERIVED from a GSTIN (whose first two digits are
// the state code) and never typed alongside one.
//
// See the server copy for the full reasoning, including why the retired codes
// are here.

export const INDIA_STATES = [
  { code: '01', name: 'Jammu and Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  // Retired on the 2020 merger, but still on registrations issued before it.
  { code: '25', name: 'Daman and Diu', retired: true, supersededBy: '26' },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: '27', name: 'Maharashtra' },
  // Retired at the Telangana bifurcation (36 Telangana, 37 the new AP).
  { code: '28', name: 'Andhra Pradesh (pre-bifurcation)', retired: true, supersededBy: '37' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
  { code: '97', name: 'Other Territory' },
  { code: '99', name: 'Centre Jurisdiction' },
];

const BY_CODE = new Map(INDIA_STATES.map((s) => [s.code, s]));

const normName = (v) =>
  String(v || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z]/g, '');

const BY_NAME = new Map();
for (const s of INDIA_STATES) {
  if (!BY_NAME.has(normName(s.name))) BY_NAME.set(normName(s.name), s);
}
const NAME_ALIASES = {
  odisha: '21', orissa: '21',
  uttarakhand: '05', uttaranchal: '05',
  puducherry: '34', pondicherry: '34',
  delhi: '07', newdelhi: '07', nctofdelhi: '07', delhinct: '07',
  tamilnadu: '33',
  jammuandkashmir: '01', jandk: '01',
  dadraandnagarhaveliandamananddiu: '26', dadraandnagarhaveli: '26',
  andamanandnicobarislands: '35', andamanandnicobar: '35',
  chattisgarh: '22', chhattisgarh: '22',
};

// '7' → '07'. Anything that isn't a real code comes back as '' — a half-valid
// code stored is worse than none, because it looks filled in.
export function normalizeStateCode(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (!/^\d{1,2}$/.test(raw)) return '';
  const padded = raw.padStart(2, '0');
  return BY_CODE.has(padded) ? padded : '';
}

export const stateByCode = (code) => BY_CODE.get(normalizeStateCode(code)) || null;
export const stateNameByCode = (code) => stateByCode(code)?.name || '';

export function stateByName(name) {
  const key = normName(name);
  if (!key) return null;
  const direct = BY_NAME.get(key);
  if (direct) return direct;
  const aliased = NAME_ALIASES[key];
  return aliased ? BY_CODE.get(aliased) : null;
}
export const stateCodeByName = (name) => stateByName(name)?.code || '';

// A GSTIN carries its own state code in digits 1-2. This is why the code is
// never a second field beside a GSTIN.
export function stateCodeFromGstin(gstin) {
  const raw = String(gstin || '').trim().toUpperCase();
  if (raw.length < 2) return '';
  return normalizeStateCode(raw.slice(0, 2));
}
export const stateFromGstin = (gstin) => stateByCode(stateCodeFromGstin(gstin));

// What a picker offers: everything still issuable, in code order — the order
// GST forms and returns use, so it is the order people scan.
export const SELECTABLE_STATES = INDIA_STATES.filter((s) => !s.retired);

// '27 — Maharashtra'. One label everywhere.
export const stateLabel = (code) => {
  const s = stateByCode(code);
  return s ? `${s.code} — ${s.name}` : '';
};
